import React, {useCallback, useEffect, useState} from 'react'
import {Form} from '@modifi/plugin-forms'
import {CustomerUploadManager} from '@modifi/plugin-documents'
import {useTranslation} from '@modifi/plugin-i18n'
import {Col, DangerAlert, H5, Row, SuccessAlert, ButtonLink, PreviousIcon} from '@modifi/ui'
import {generatePath} from '@modifi/router'

import {LegalRepresentativeFormData} from '../constants'
import {ROUTE_COMPANY_PROFILE_LEGAL_REPRESENTATIVES} from '../../../../routes/constants'
import LegalRepresentativesFormContent from '../components/legal-rep-form-content'
import SignatoryFieldWatcher from '../components/signatory-field-watcher'
import PersonFormFooter from '../../components/person-form-footer'
import {FormContentWrapper} from '../../../../ui/form'
import {EntityFormData} from '../../../../types/common'
import {CompanyUnlockRefresher} from '../../../../plugins/onboarding/components/company-unlock-refresher'
import {aFetchLegalRepresentatives} from '../../../../data/legal-representatives'
import {aFetchDocumentsByExternalPersonId} from '../../../../data/person-documents'

type ExistingLegalRepresentativeProps = {
  isLoading: boolean
  updatePending: boolean
  initialize: (externalCompanyId: string, externalPersonId: string) => void
  externalCompanyId: string
  externalPersonId: string
  formData: EntityFormData<LegalRepresentativeFormData>
  onSubmit: (
    changes: Record<string, unknown>,
    externalCompanyId: string,
    externalPersonId: string
  ) => void
  onDiscard: () => void
  hasError: boolean
  errorMessage?: string
  redirect: (route: string) => void
  isValidPersonId: boolean
  pageIsVerified: boolean
  hasAnyRoleBasedFieldsVisible: boolean
  legalRepName: string
}

const ExistingLegalRepresentative: React.FC<ExistingLegalRepresentativeProps> = ({
  updatePending,
  externalPersonId,
  externalCompanyId,
  hasError,
  errorMessage,
  isLoading,
  onSubmit,
  initialize,
  formData,
  onDiscard,
  redirect,
  isValidPersonId,
  pageIsVerified,
  hasAnyRoleBasedFieldsVisible,
  legalRepName,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const [currentPage, setCurrentPage] = useState(1)
  const [shouldRenderSignatoryComponents, setshouldRenderSignatoryComponents] = useState(false)

  // without the utility bill upload the flow ends one page earlier
  const isFinalStep = !shouldRenderSignatoryComponents || currentPage === 2

  useEffect(() => {
    initialize(externalCompanyId, externalPersonId)
  }, [initialize, externalCompanyId, externalPersonId])

  useEffect(() => {
    onDiscard()
  }, [onDiscard])

  useEffect(() => {
    if (!isValidPersonId) {
      redirect(generatePath(ROUTE_COMPANY_PROFILE_LEGAL_REPRESENTATIVES))
    }
  }, [isValidPersonId, redirect, externalCompanyId])

  const handleSubmit = useCallback(
    (changes: Record<string, any>) => {
      if (!isFinalStep && currentPage === 1) {
        setCurrentPage(currentPage + 1)
      } else {
        onSubmit(changes, externalCompanyId, externalPersonId)
      }
    },
    [onSubmit, externalCompanyId, externalPersonId, currentPage, isFinalStep]
  )

  const getSubmitButtonLabel = useCallback(() => {
    if (isFinalStep) {
      return t(`sub-section-footer.cta-submit`)
    }
    return t('sub-section-footer.cta-next')
  }, [isFinalStep, t])

  const handleBackButtonClick = useCallback(() => {
    setCurrentPage(currentPage - 1)
  }, [currentPage])

  if (isLoading) {
    return null
  }

  return (
    <CustomerUploadManager>
      {pageIsVerified && (
        <SuccessAlert>{t('legal-representative-page.fully-verified-alert')}</SuccessAlert>
      )}

      {hasError && errorMessage && <DangerAlert>{errorMessage}</DangerAlert>}

      <Row>
        <Col>
          <ButtonLink
            data-target="legal-rep-summary-page"
            onClick={() => redirect(ROUTE_COMPANY_PROFILE_LEGAL_REPRESENTATIVES)}
          >
            <PreviousIcon /> {t('summary-page.back-button.label')}
          </ButtonLink>
        </Col>
      </Row>

      <Row>
        <Col mb={40}>
          <H5>{legalRepName}</H5>
        </Col>
      </Row>

      <Form
        isLoading={updatePending}
        // @ts-expect-error handling of null values
        defaultValues={formData.defaultValues}
        // @ts-expect-error handling of null values
        initialValues={formData.initialValues}
        onSubmit={handleSubmit}
        onDiscard={onDiscard}
      >
        <FormContentWrapper>
          <LegalRepresentativesFormContent
            t={t}
            isNewLegalRepresentative={false}
            selectPersonAsDataSource={() => null}
            dataSourceOptions={[]}
            currentPage={currentPage}
            hasAnyRoleFieldItem={hasAnyRoleBasedFieldsVisible}
          />
        </FormContentWrapper>

        <SignatoryFieldWatcher onVisibilityChange={setshouldRenderSignatoryComponents} />

        <CompanyUnlockRefresher
          refetch={(): any[] => [
            aFetchLegalRepresentatives(),
            aFetchDocumentsByExternalPersonId(externalPersonId),
          ]}
          initialValues={formData.initialValues}
          defaultValues={formData.defaultValues}
        />
        <PersonFormFooter
          loading={updatePending}
          hasError={hasError}
          submitLabel={getSubmitButtonLabel()}
          canMoveBack={currentPage !== 1}
          handleBackButtonClick={handleBackButtonClick}
          isFinalStep={isFinalStep}
        />
      </Form>
    </CustomerUploadManager>
  )
}

export default ExistingLegalRepresentative
